import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { ApiHelper, RmaApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import _ from "lodash";

const countyCommodityKey = ({ state_name, county_name, commodity_name, crop_year }) =>
  `${state_name}|${county_name}|${commodity_name}|${crop_year}`;

const calculateWeightedDatanacAverage = (data) => {
  if (!data?.length) return null;

  const sortedData = [...data].sort(
    (a, b) => new Date(b.week_ending_date) - new Date(a.week_ending_date)
  );

  const alpha = 0.5;
  let weightedSum = 0;
  let weightSum = 0;

  sortedData.forEach((item, index) => {
    const weight = Math.exp(-alpha * index);
    weightedSum += (item.predicted_yield || 0) * weight;
    weightSum += weight;
  });

  return weightSum > 0 ? weightedSum / weightSum : null;
};

const loadYieldForecastForCounty = async ({ state_name, county_name, commodity_name, crop_year, globalState }) => {
  const response = await ApiHelper.callSmociObject(
    "data/DATANAC",
    "Gridmet",
    "YieldForecast",
    "Search",
    { state_name, county_name, crop_year, commodity_name },
    globalState
  );

  const rows = response || [];
  const irrigatedData = rows.filter((item) => item.practice_name === "Irrigated");
  const nonIrrigatedData = rows.filter((item) => item.practice_name === "Non-Irrigated");

  return {
    datanacIrrigated: calculateWeightedDatanacAverage(irrigatedData),
    datanacNonIrrigated: calculateWeightedDatanacAverage(nonIrrigatedData),
  };
};

const loadRmaCountyYields = async ({ state_name, county_name, commodity_name, crop_year }) => {
  const rmaParams = {
    state_name,
    county_name,
    reinsurance_year: crop_year,
    commodity_name,
  };

  const response = await RmaApiHelper.selectCountyData(rmaParams);
  const filteredResponse = response.filter((item) => {
    if (commodity_name === "corn") {
      return item.type_name === "Grain";
    }
    return true;
  });

  const nonIrrigated = filteredResponse.find((item) => item.practice_name.includes("Non-Irrigated"));
  const irrigated = filteredResponse.find(
    (item) => item.practice_name.includes("Irrigated") && !item.practice_name.includes("Non-Irrigated")
  );

  return {
    rmaProjectedIrrigated: irrigated?.expected_index_value ?? null,
    rmaProjectedNonIrrigated: nonIrrigated?.expected_index_value ?? null,
  };
};

const loadRmaHistoricalYields = async ({ state_name, county_name, commodity_name, crop_year }) => {
  const response = await RmaApiHelper.selectHistoricalYieldTrend({
    state_name,
    county_name,
    commodity_name,
    yield_year: crop_year,
    reinsurance_year: getCurrentReinsuranceYear(),
  });

  const filteredResponse = response.filter((item) => {
    if (commodity_name === "corn") {
      return item.type_name === "Grain";
    }
    return true;
  });

  const nonIrrigated = filteredResponse.find((item) => item.practice_name.includes("Non-Irrigated"));
  const irrigated = filteredResponse.find(
    (item) => item.practice_name.includes("Irrigated") && !item.practice_name.includes("Non-Irrigated")
  );

  return {
    rmaHistoricalIrrigated: irrigated?.yield_amount ?? null,
    rmaHistoricalNonIrrigated: nonIrrigated?.yield_amount ?? null,
  };
};

const weightedMean = (entries, valueKey, acresKey = "acres") => {
  const valid = entries.filter((e) => e[valueKey] != null && e[acresKey] > 0);
  if (!valid.length) return null;
  const totalAcres = _.sumBy(valid, acresKey);
  if (!totalAcres) return null;
  return _.sumBy(valid, (e) => e[valueKey] * e[acresKey]) / totalAcres;
};

export function useYieldForecastAggregate({ budgetCountyPractices, crop_year, apiConfig = null }) {
  const { globalState: contextGlobalState } = useContext(AppContext);
  const globalState = apiConfig || contextGlobalState;

  const [byCounty, setByCounty] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const cacheRef = useRef(new Map());

  const countyCommodityRequests = useMemo(() => {
    if (!budgetCountyPractices?.length) return [];

    const grouped = _.groupBy(
      budgetCountyPractices,
      (bcp) =>
        `${bcp.state_name}|${bcp.county_name}|${bcp.commodity_name}|${bcp.type_name || ""}|${bcp.practice_name || ""}`
    );

    return Object.values(grouped).map((rows) => {
      const first = rows[0];
      return {
        state_name: first.state_name,
        county_name: first.county_name,
        commodity_name: first.commodity_name,
        type_name: first.type_name,
        practice_name: first.practice_name,
        acres: _.sumBy(rows, (r) => r.reported_acreage || 0),
      };
    });
  }, [budgetCountyPractices]);

  const uniqueCountyCommodities = useMemo(() => {
    const map = new Map();
    countyCommodityRequests.forEach((row) => {
      const key = countyCommodityKey({
        state_name: row.state_name,
        county_name: row.county_name,
        commodity_name: row.commodity_name,
        crop_year,
      });
      if (!map.has(key)) {
        map.set(key, {
          state_name: row.state_name,
          county_name: row.county_name,
          commodity_name: row.commodity_name,
        });
      }
    });
    return [...map.values()];
  }, [countyCommodityRequests, crop_year]);

  const loadAll = useCallback(async () => {
    if (!crop_year || !uniqueCountyCommodities.length || !globalState?.producer_token) {
      setByCounty([]);
      return;
    }

    setIsLoading(true);
    try {
      const countyRows = [];

      for (const req of uniqueCountyCommodities) {
        const key = countyCommodityKey({ ...req, crop_year });
        let cached = cacheRef.current.get(key);

        if (!cached) {
          const [datanac, rmaProjected, rmaHistorical] = await Promise.all([
            loadYieldForecastForCounty({ ...req, crop_year, globalState }),
            loadRmaCountyYields({ ...req, crop_year }),
            loadRmaHistoricalYields({ ...req, crop_year }),
          ]);
          cached = { ...datanac, ...rmaProjected, ...rmaHistorical };
          cacheRef.current.set(key, cached);
        }

        const acresForCountyCommodity = _.sumBy(
          countyCommodityRequests.filter(
            (r) =>
              r.state_name === req.state_name &&
              r.county_name === req.county_name &&
              r.commodity_name === req.commodity_name
          ),
          "acres"
        );

        countyRows.push({
          state_name: req.state_name,
          county_name: req.county_name,
          commodity_name: req.commodity_name,
          acres: acresForCountyCommodity,
          ...cached,
        });
      }

      setByCounty(countyRows);
    } catch (err) {
      console.warn("Error loading yield forecast aggregate:", err);
      setByCounty([]);
    } finally {
      setIsLoading(false);
    }
  }, [crop_year, uniqueCountyCommodities, countyCommodityRequests, globalState?.producer_token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const byCommodity = useMemo(() => {
    const grouped = _.groupBy(byCounty, "commodity_name");
    const result = {};

    Object.entries(grouped).forEach(([commodity_name, rows]) => {
      result[commodity_name] = {
        weightedDatanacIrrigated: weightedMean(rows, "datanacIrrigated"),
        weightedDatanacNonIrrigated: weightedMean(rows, "datanacNonIrrigated"),
        weightedRmaProjectedIrrigated: weightedMean(rows, "rmaProjectedIrrigated"),
        weightedRmaProjectedNonIrrigated: weightedMean(rows, "rmaProjectedNonIrrigated"),
        weightedRmaHistoricalIrrigated: weightedMean(rows, "rmaHistoricalIrrigated"),
        weightedRmaHistoricalNonIrrigated: weightedMean(rows, "rmaHistoricalNonIrrigated"),
        breakdown: rows,
        totalAcres: _.sumBy(rows, "acres"),
      };
    });

    return result;
  }, [byCounty]);

  const byCountyMap = useMemo(() => {
    const map = {};
    byCounty.forEach((row) => {
      map[countyCommodityKey({ ...row, crop_year })] = row;
    });
    return map;
  }, [byCounty, crop_year]);

  return {
    isLoading,
    byCounty,
    byCountyMap,
    byCommodity,
  };
}

export default useYieldForecastAggregate;
